import IdsHelper from '@d-shared/helpers/ids-helper/ids-helper';
import trackingService from '@d-services/tracking-service/tracking-service';
import { pascalToSnakeCase } from '@d-shared/utils/pascal-to-snake-case';
import { addObjectToFormData } from '@d-shared/forms/forms';
import Window from '@d-shared/utils/window';

export class EventsTracking {
  constructor() {
    this._pageEventDate = Date.now();
    this._pageEvent = null;
    this._refererUrl = null;
    this._deviceFingerprint = null;
    this._userUuid = null;
    this._parentUuid = null;

    this._extraParentParams = {};

    this._trackingEvent = null;
    this.currentChainId = null;

    this._sendEvent = this._sendEvent.bind(this);
    this._init();
  }

  _init() {
    this._collectInitialData();
    this._sendParentEvent();
  }

  _collectInitialData() {
    const trackingElement = document.querySelector('#tracking-element');

    if (!trackingElement) {
      return;
    }

    this._deviceFingerprint = this._getParamValue(trackingElement.dataset.deviceFingerprint);
    this._pageEvent = this._getParamValue(trackingElement.dataset.pageEvent);
    this._refererUrl = this._getParamValue(trackingElement.dataset.refererUrl);
    this._userUuid = this._getParamValue(trackingElement.dataset.userUuid);
    this._parentUuid = IdsHelper.getNewUuid();

    const extra = this._getParamValue(trackingElement.dataset.extra);

    if (extra) {
      const extraParams = JSON.parse(extra);

      Object.entries(extraParams).forEach(([ key, value ]) => {
        this._extraParentParams[key] = value;
      });
    }

    trackingElement.remove();
  }

  _getParamValue(param) {
    return param === 'undefined' || !param ? null : `${param}`;
  }

  _sendParentEvent() {
    if (!this._pageEvent) {
      return;
    }

    const formData = new FormData();

    const dataObject = {
      name: this._pageEvent,
      happened_at_epoch: this._pageEventDate,
      device_fingerprint: this._deviceFingerprint,
      user_uuid: this._userUuid,
      referer_url: this._refererUrl,
      uuid: this._parentUuid,
    };

    addObjectToFormData(dataObject, formData);
    addObjectToFormData(this._extraParentParams, formData);

    trackingService.sendEvent(formData);
  }

  _sendEvent() {
    if (!this._parentUuid) {
      return;
    }

    const formData = new FormData();

    const dataObject = {
      parent_uuid: this._parentUuid,
      device_fingerprint: this._deviceFingerprint,
      user_uuid: this._userUuid,
    };

    Object.entries(this._trackingEvent).forEach(([ chainKey, chainValue ]) => {
      dataObject[chainKey] = chainValue;
    });

    addObjectToFormData(dataObject, formData);
    const result = trackingService.sendEvent(formData);

    if (!result) return;

    this._trackingEvent = null;
  }

  _addDataToObject(targetObject, key, value) {
    const formattedKey = pascalToSnakeCase(key);
    targetObject[formattedKey] = value;
  }

  _formatContext(context = {}) {
    const formattedObject = {};

    Object.keys(context).forEach((key) => {
      this._addDataToObject(formattedObject, key, context[key]);
    });

    return formattedObject;
  }

  _prepareEventObject(name, uuid, context) {
    return Object.assign(
      {
        name,
        happened_at_epoch: Date.now(),
        chain_uuid: uuid,
      },
      this._formatContext(context),
    );
  }

  startNewChain(name, context, callback) {
    const uuid = IdsHelper.getNewUuid();
    const event = this._prepareEventObject(name, uuid, context);

    this.currentChainId = uuid;
    this._trackingEvent = event;

    this._sendEvent();

    if (!callback) return;

    callback();
  }

  startNewChainId() {
    this.currentChainId = IdsHelper.getNewUuid();

    return this.currentChainId;
  }

  addEventToCurrentChain(name, context, callback) {
    if (!this.currentChainId) {
      this.currentChainId = IdsHelper.getNewUuid();
    }

    this._trackingEvent = this._prepareEventObject(name, this.currentChainId, context);

    this._sendEvent();

    if (!callback) return;

    callback();
  }

  trackAndRedirect(name, context, url, isNewChain = false, callback) {
    if (isNewChain) {
      this.startNewChain(name, context, callback);
    } else {
      this.addEventToCurrentChain(name, context, callback);
    }

    const urlWithExtraParam = new URL(url, window.location.origin);
    const params = new URLSearchParams(urlWithExtraParam.search);
    params.append('tracking_chain_uuid', this.currentChainId);
    urlWithExtraParam.search = params.toString();

    const redirectUrl = this._pageEvent ? urlWithExtraParam.toString() : url;

    Window.goTo(redirectUrl);
  }

  trackAndSubmitForm(name, context, isNewChain = false, callback) {
    if (isNewChain) {
      this.startNewChain(name, context);
    } else {
      this.addEventToCurrentChain(name, context);
    }

    return true;
  }
}


export default {
  install: function(Vue, options) {
    Vue.prototype.eventsTracking = new EventsTracking();
  },
};
